
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNotificationForm } from "./useNotificationForm";
import { createNotificationInDb } from "@/services/notificationService";
import { AIAnalysisResult, NotificationProductData } from "@/types/notifications";
import { useIsMobile } from "@/hooks/use-mobile";

export const useCreateNotification = (userId: string | undefined, userName: string | undefined, onSuccess: () => void) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  
  const {
    notificationForm: newNotification,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleInternalCheckboxChange,
    toggleExternalRecipient,
    handleProductsChange,
    handleAIAnalysisComplete: onFormAnalysisComplete
  } = useNotificationForm(userId, userName);

  const createNotificationMutation = useMutation({
    mutationFn: async () => {
      console.log("Starting notification creation with form data:", newNotification);
      return createNotificationInDb(newNotification);
    },
    onSuccess: (data) => {
      console.log("Notification created successfully:", data);
      
      // Force immediate invalidation and refetch of all notification queries
      console.log("Forcing immediate refresh of notification queries...");
      
      // First invalidate all relevant queries to mark them as stale
      queryClient.invalidateQueries({ queryKey: ['notifications', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count', 'unread'] });
      
      // Then explicitly force a refetch to ensure immediate UI update
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ['notifications', 'active'] });
        queryClient.refetchQueries({ queryKey: ['notifications-count', 'active'] });
        queryClient.refetchQueries({ queryKey: ['notifications-count', 'unread'] });
      }, 100);
      
      // Show success message
      toast.success("Melding aangemaakt");
      
      // Remove form-submitting class if on mobile
      if (isMobile) {
        document.body.classList.remove('form-submitting');
      }
      
      // Call the provided success callback
      onSuccess();
    },
    onError: (error: any) => {
      console.error("Error in createNotification mutation:", error);
      
      // Remove form-submitting class if on mobile
      if (isMobile) {
        document.body.classList.remove('form-submitting');
      }
      
      toast.error("Fout bij het aanmaken van melding", {
        description: error.message
      });
    }
  });

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Make sure to clean up the class when component unmounts
      document.body.classList.remove('form-submitting');
    };
  }, []);

  const handleAIAnalysisComplete = (data: AIAnalysisResult) => {
    onFormAnalysisComplete(data);
    setIsAnalyzing(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate products if Inkoop Goes is selected
    if (newNotification.recipients.internal.group1) {
      const products = newNotification.products || [];
      if (!products.length) {
        toast.error("Voeg minimaal één product toe voor Inkoop Goes");
        return;
      }
      
      // Validate required product fields
      const invalidProducts = products.filter(p => 
        !p.product_name.trim() || 
        !p.product_sku.trim() || 
        !p.product_bc.trim() || 
        p.product_quantity <= 0
      );
      
      if (invalidProducts.length > 0) {
        toast.error("Vul alle verplichte productvelden in");
        return;
      }
    }
    
    console.log("Form submitted with data:", newNotification);
    createNotificationMutation.mutate();
  };

  return {
    newNotification,
    isAnalyzing,
    setIsAnalyzing,
    isPending: createNotificationMutation.isPending,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleInternalCheckboxChange,
    toggleExternalRecipient,
    handleProductsChange,
    handleAIAnalysisComplete,
    handleSubmit
  };
};
